import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { ArrowClockwise, SpinnerGap } from "@phosphor-icons/react";
import { confirmDialog } from "../../lib/uiDialogs";
import { API_BASE as API } from "../../lib/apiBase";

/**
 * Vincent B890 (Eric 2026-02-14 SPEC STRICK PROD) — Rectangles admin
 * déplacés directement SOUS la vidéo (dans VideoDetailPage) pour rester
 * visibles quel que soit l'onglet actif du panneau de droite. Ordre demandé :
 *   1. Debug fondateur (fondateur only) — inclut désormais l'ID de la vidéo
 *   2. Corriger la vidéo (admin/fondateur)
 *   3. Fireflies déjà disponible (rendu ailleurs par VideoDetailPage)
 *
 * Ce composant est autonome : il possède ses propres handlers pour
 * `/optimize` et `/reprocess`, indépendants de ceux de SpeakersPanel
 * (qui héberge le workflow diarisation). Aucun état partagé nécessaire.
 */
export default function VideoActionsCards({
  video,
  videoId,
  isAdmin,
  isFounder,
  lang,
  useDiarisation,
  useFacialId,
  onDataChanged,
}) {
  const [reoptimizing, setReoptimizing] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);

  const handleReoptimizeVideo = async () => {
    if (!videoId) return;
    const ok = await confirmDialog(
      lang === "fr"
        ? "Ré-encoder le fichier en H.264/yuv420p ? Utile si le player affiche un écran noir (codec incompatible)."
        : "Re-encode the file to H.264/yuv420p? Useful when the player shows a black screen.",
      { confirmLabel: lang === "fr" ? "Ré-encoder" : "Re-encode", tone: "warning" }
    );
    if (!ok) return;
    setReoptimizing(true);
    try {
      await axios.post(`${API}/api/videos/${videoId}/optimize`, {}, { withCredentials: true });
      toast.success(lang === "fr" ? "Ré-optimisation FFmpeg lancée." : "FFmpeg re-optimization started.");
      onDataChanged?.();
    } catch (e) {
      toast.error(
        (lang === "fr" ? "Échec de la ré-optimisation : " : "Re-optimization failed: ") +
        (e?.response?.data?.detail || e?.message || "erreur inconnue")
      );
    } finally {
      setReoptimizing(false);
    }
  };

  const handleReprocessPipeline = async () => {
    if (!videoId) return;
    const ok = await confirmDialog(
      lang === "fr"
        ? "Relancer TOUT le pipeline (transcription + diarisation + extraction) depuis le début ? Cela remplacera la transcription et les connaissances actuelles."
        : "Restart the ENTIRE pipeline from scratch? Replaces current transcript and knowledge.",
      { danger: true, confirmLabel: lang === "fr" ? "Relancer TOUT" : "Restart ALL" }
    );
    if (!ok) return;
    setReprocessing(true);
    try {
      await axios.post(
        `${API}/api/videos/${videoId}/reprocess`,
        { use_diarisation: !!useDiarisation, use_facial_id: !!useFacialId },
        { withCredentials: true }
      );
      toast.success(lang === "fr" ? "Pipeline relancé — traitement en cours." : "Pipeline restarted — processing.");
      onDataChanged?.();
    } catch (e) {
      toast.error(
        (lang === "fr" ? "Échec du relancement : " : "Restart failed: ") +
        (e?.response?.data?.detail || e?.message || "erreur inconnue")
      );
    } finally {
      setReprocessing(false);
    }
  };

  const copyDebugBundle = async () => {
    if (!videoId) return;
    try {
      const { data } = await axios.get(
        `${API}/api/admin/videos/${videoId}/debug-bundle`,
        { withCredentials: true }
      );
      const jsonStr = JSON.stringify(data, null, 2);
      try {
        await navigator.clipboard.writeText(jsonStr);
        const nLogs = data.pipeline_logs_count || 0;
        const nSeg = data.segments_count || 0;
        const spkList = Object.keys(data.speaker_distribution || {}).join(", ") || "aucun";
        toast.success(`Bundle copié (${nLogs} logs, ${nSeg} segments, locuteurs: ${spkList})`);
      } catch {
        const blob = new Blob([jsonStr], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        toast.info(lang === "fr" ? "Presse-papier indisponible — fichier ouvert" : "Clipboard unavailable — file opened");
      }
    } catch (e) {
      const status = e?.response?.status || "?";
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Erreur ${status} : ${detail}`.slice(0, 200));
    }
  };

  const copyVideoId = async () => {
    if (!videoId) return;
    try {
      await navigator.clipboard.writeText(videoId);
      toast.success(lang === "fr" ? `ID copié : ${videoId}` : `ID copied: ${videoId}`);
    } catch {
      toast.info(lang === "fr" ? "Copie impossible — sélectionnez manuellement." : "Copy failed — select manually.");
    }
  };

  const pipelineActive = ["queued", "processing"].includes(video?.pipeline_status);
  const reprocessDisabled = reprocessing || pipelineActive;

  return (
    <div className="space-y-3" data-testid="video-actions-cards">
      {/* ── 1. Debug fondateur (fondateur only) ─────────────────────── */}
      {isFounder && videoId && (
        <div
          className="p-3 bg-slate-50 border border-slate-300 rounded-lg space-y-2"
          data-testid="video-debug-founder-card"
        >
          <p className="text-[11px] font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5">
            💉 {lang === "fr" ? "Debug fondateur (ticket #500)" : "Founder debug (#500)"}
          </p>
          {/* Vincent B890 — Affichage + copie rapide de l'ID vidéo, pour éviter d'ouvrir le bundle. */}
          <div
            className="flex items-center gap-2 p-1.5 bg-white border border-slate-200 rounded font-mono text-[10px] text-slate-700"
            data-testid="video-debug-founder-id-row"
          >
            <span className="opacity-60 flex-shrink-0">ID :</span>
            <code className="flex-1 truncate select-all" data-testid="video-debug-founder-id-value">
              {videoId}
            </code>
            <button
              type="button"
              onClick={copyVideoId}
              className="text-slate-600 hover:text-slate-900 text-[10px] font-semibold px-1.5 py-0.5 border border-slate-300 rounded bg-white hover:bg-slate-50"
              data-testid="video-debug-founder-copy-id-btn"
              title={lang === "fr" ? "Copier l'ID" : "Copy ID"}
            >
              📋
            </button>
          </div>
          <p className="text-[10px] text-slate-600 leading-snug">
            {lang === "fr"
              ? "Copie dans le presse-papier un JSON complet (video doc, pipeline_logs, speaker distribution). À coller dans le chat pour investigation."
              : "Copies to clipboard a full JSON (video doc, pipeline_logs, speaker distribution). Paste in chat for investigation."}
          </p>
          <Button
            size="sm"
            variant="outline"
            className="w-full gap-1.5 text-xs border-slate-400 text-slate-700 hover:bg-slate-100"
            data-testid="video-debug-founder-copy-bundle-btn"
            onClick={copyDebugBundle}
          >
            📋 {lang === "fr" ? "Copier bundle debug" : "Copy debug bundle"}
          </Button>
        </div>
      )}

      {/* ── 2. Corriger la vidéo (admin/fondateur) ──────────────────── */}
      {(isAdmin || isFounder) && videoId && (
        <div
          className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2"
          data-testid="video-correction-card"
        >
          <p className="text-xs font-semibold text-amber-900 flex items-center gap-1.5">
            <ArrowClockwise size={13} />
            {lang === "fr" ? "Corriger la vidéo" : "Fix the video"}
          </p>
          <p className="text-[11px] text-amber-800 leading-snug">
            {lang === "fr"
              ? "Utilise ces actions si la vidéo affiche un écran noir (ré-optimisation) ou si la transcription/diarisation semble incorrecte (relance complète du pipeline)."
              : "Use these when the player shows a black screen (re-optimize) or when the transcript looks wrong (full pipeline restart)."}
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={handleReoptimizeVideo}
              disabled={reoptimizing}
              className="flex-1 gap-1.5 text-xs text-rose-700 border-rose-300 hover:bg-rose-50"
              data-testid="video-correction-reoptimize-btn"
              title={
                lang === "fr"
                  ? "Ré-encoder le fichier en H.264/yuv420p (règle les écrans noirs)"
                  : "Re-encode the file to H.264/yuv420p (fixes black screens)"
              }
            >
              {reoptimizing ? <SpinnerGap size={12} className="animate-spin" /> : <ArrowClockwise size={12} />}
              {lang === "fr" ? "Ré-optimiser le fichier" : "Re-optimize file"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleReprocessPipeline}
              disabled={reprocessDisabled}
              className={`flex-1 gap-1.5 text-xs ${
                reprocessDisabled
                  ? "text-zinc-400 border-zinc-200 bg-zinc-50 cursor-not-allowed opacity-60"
                  : "text-amber-800 border-amber-400 hover:bg-amber-100"
              }`}
              data-testid="video-correction-reprocess-btn"
              title={
                pipelineActive
                  ? lang === "fr"
                    ? "Pipeline en cours — indisponible tant que la transcription tourne."
                    : "Pipeline running — unavailable while transcribing."
                  : lang === "fr"
                    ? "Repart de zéro : Whisper → diarisation → extraction GPT (remplace tout)"
                    : "Restart from scratch: Whisper → diarization → GPT extraction (replaces everything)"
              }
            >
              {reprocessing ? <SpinnerGap size={12} className="animate-spin" /> : <ArrowClockwise size={12} />}
              {lang === "fr" ? "Relancer TOUT le pipeline" : "Restart ENTIRE pipeline"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
